/* eslint-disable jsx-a11y/label-has-associated-control */
// Eslint not happy with dynamic fields and htmlFor :(
import classNames from 'classnames';
import styles from './DateTime.module.scss';

import React from 'react';
import _get from 'lodash/get';
import { Field, useFormikContext } from 'formik';
import { FaRegCalendar } from 'react-icons/fa';
import { MdOutlineAccessTimeFilled } from 'react-icons/md';

import Error from './Error';
import * as datetime from 'legacy/common/utils/datetime';
import DayPickerField from 'legacy/components/util/FormHelpers/DayPickerField';
import TimeOnlyPickerField from 'legacy/components/util/FormHelpers/TimeOnlyPickerField';

const DateField = ({ field, form, onChange, disabled }) => (
  <input
    type="date"
    className={classNames(styles.datepicker, disabled && styles.disabled)}
    name={field.name}
    value={datetime.format(field.value, 'YYYY-MM-DD')}
    onChange={(e) => {
      form.setFieldValue(field.name, datetime.toISOString(e.target.value));

      if (onChange) {
        onChange();
      }
    }}
    onBlur={field.onBlur}
    disabled={disabled}
    min={datetime.format(new Date(), 'YYYY-MM-DD')}
  />
);

const TimeField = ({ field, form, onChange, disabled }) => (
  <input
    type="time"
    className={classNames(styles.datepicker, disabled && styles.disabled)}
    name={field.name}
    value={datetime.format(field.value, 'HH:mm')}
    onChange={(e) => {
      const [hour, minute] = e.target.value.split(':');

      form.setFieldValue(
        field.name,
        datetime.toISOString(
          datetime.set(form.values[field.name], {
            hour: parseInt(hour),
            minute: parseInt(minute)
          })
        )
      );

      if (onChange) {
        onChange();
      }
    }}
    disabled={disabled}
    onBlur={field.onBlur}
  />
);

interface DateTimeProps {
  name: string;
  datePlaceholder: string;
  timePlaceholder: string;
  dateLabel?: string;
  timeLabel?: string;
  min?: string | Date;
  max?: string | Date;
  startDate?: string;
  inputStyle?: InputStyles;
  onChange?: () => void;
  disabled?: boolean;
}

export enum InputStyles {
  Default = 'default',
  Input = 'input'
}

// TODO: This would greatly benefit from a compound architecture to be able to really customize everything
const DateTime: React.FC<DateTimeProps> = ({
  name,
  datePlaceholder,
  timePlaceholder,
  min,
  max,
  startDate,
  dateLabel,
  timeLabel,
  inputStyle = InputStyles.Default,
  onChange,
  disabled = false
}) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<any>();

  const showError = !!_get(errors, name) && !!_get(touched, name);

  React.useEffect(() => {
    if (startDate) {
      setFieldValue(name, datetime.toISOString(startDate));
    }
  }, [startDate, name, setFieldValue]);

  return (
    <>
      <div
        className={classNames(
          inputStyle === InputStyles.Default && 'mt-10',
          inputStyle === InputStyles.Input && 'gap-5',
          'flex',
          styles.datetimePicker,
          {
            'bg-red-100 p-1 rounded-md': showError
          }
        )}
      >
        <div
          className={classNames(inputStyle === InputStyles.Input && 'flex-1')}
        >
          {inputStyle === InputStyles.Input && (
            <label className="text-base font-normal" htmlFor={name}>
              {dateLabel}
            </label>
          )}
          <div
            className={classNames(
              !values[name] && !disabled && 'text-gray-500',
              'text-base flex items-center',
              inputStyle === InputStyles.Default && 'mr-4 md:mr-10',
              inputStyle === InputStyles.Input &&
                'px-3 py-4 mt-1 border-1 rounded-md',
              disabled
                ? 'bg-neutral-100 text-gray-400'
                : 'cursor-pointer border-neutral-300'
            )}
          >
            <FaRegCalendar size={20} className="mr-2 text-gray-500" />
            <div
              className={classNames(
                inputStyle === InputStyles.Input && 'flex-1',
                'relative'
              )}
            >
              {values[name]
                ? datetime.format(
                    values[name],
                    inputStyle === InputStyles.Input
                      ? 'YYYY-MM-DD'
                      : 'dddd, MMM D, YYYY'
                  )
                : datePlaceholder}
              {/*
               * Note:
               *  - Native datepicker is used for mobile view
               *  - React Datepicker library is used for desktop view
               */}
              <div className="absolute left-0 top-0 w-full">
                <div className="block md:hidden">
                  <Field
                    id={name}
                    name={name}
                    component={DateField}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>

                <div className="hidden md:block">
                  <DayPickerField
                    name={name}
                    className="border-0 opacity-0"
                    placeholderText="YYY-MM-DD"
                    disabledBefore={new Date(min)}
                    disabledAfter={new Date(max)}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={classNames(inputStyle === InputStyles.Input && 'flex-1')}
        >
          {inputStyle === InputStyles.Input && (
            <label className="text-base font-normal" htmlFor={`${name}Time`}>
              {timeLabel}
            </label>
          )}
          <div
            className={classNames(
              !values[name] && !disabled && 'text-gray-500',
              'text-base flex items-center',
              inputStyle === InputStyles.Default && 'mr-4 md:mr-10',
              inputStyle === InputStyles.Input &&
                'px-3 py-4 mt-1 border-1 rounded-md',
              disabled
                ? 'bg-neutral-100 text-gray-400'
                : 'cursor-pointer border-neutral-300'
            )}
          >
            <MdOutlineAccessTimeFilled
              size={20}
              className="mr-2 text-gray-500"
            />
            <div
              className={classNames(
                inputStyle === InputStyles.Input && 'flex-1',
                'relative pr-2'
              )}
            >
              {datetime.format(values[name], 'hh:mm a') || timePlaceholder}

              {/*
               * Note:
               *  - Native timepicker is used for mobile view
               *  - React Datepicker library is used for desktop view
               */}
              <div className="absolute left-0 top-0 w-full">
                <div className="block md:hidden">
                  <Field
                    id={name}
                    name={name}
                    component={TimeField}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>

                <div className="hidden md:block">
                  <TimeOnlyPickerField
                    name={`${name}Time`}
                    className="border-0 opacity-0"
                    placeholderText="YYY-MM-DD"
                    disabledBefore={new Date(min)}
                    disabledAfter={new Date(max)}
                    compareDate={values[name]}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showError && <Error error={_get(errors, name)} />}
    </>
  );
};

export default DateTime;
